import React, { useEffect, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  Tooltip,
  Popover,
} from "@mui/material";
import InformationIcon from "../icons/InformationIcon";
import CloseIcon from "../icons/CloseIcon";
import StyledTextField from "../../styles/StyledTextField";
import { useController } from "react-hook-form";
import { PatternFormat } from "react-number-format";

const SurveyTextField = React.forwardRef(
  (
    {
      label,
      name,
      autoComplete,
      title,
      description,
      control,
      inputProps,
      maskFormat,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    const {
      field: { value, onChange, onBlur },
      fieldState: { error },
    } = useController({ name, control });

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const isPopoverOpen = Boolean(anchorEl);
    const handlePopoverOpen = (event) => setAnchorEl(event.currentTarget);
    const handlePopoverClose = useCallback(() => setAnchorEl(null), []);
    const shouldShrinkLabel = Boolean(value) || isFocused;
    const handleFieldBlur = () => {
      setIsFocused(false);
      onBlur();
    };

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          handlePopoverClose();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handlePopoverClose]);

    const InfoButton = (
      <>
        {isDesktop ? (
          <Tooltip
            arrow
            placement="top"
            enterTouchDelay={0}
            leaveTouchDelay={5000}
            componentsProps={{
              tooltip: {
                sx: {
                  color: theme.palette.text.primary,
                  borderTop: `1px solid ${theme.palette.tertiary.main}`,
                  borderBottom: `1px solid ${theme.palette.tertiary.main}`,
                  borderRadius: 0,
                  textAlign: "left",
                  backgroundColor: theme.palette.lightPurple,
                  boxShadow: theme.shadows[4],
                  maxWidth: "300px",
                  p: 3,
                },
              },
              arrow: {
                sx: { color: theme.palette.lightPurple },
              },
            }}
            title={
              <Box id={`sr-description-${name}`} role="tooltip">
                <Box display="flex" alignItems="center" mb={1}>
                  <InformationIcon
                    sx={{
                      fontSize: 24,
                      color: theme.palette.tertiary.main,
                      mr: 1,
                      mt: "7px",
                    }}
                  />
                  <Typography
                    variant="h4"
                    sx={{
                      color: theme.palette.tertiary.main,
                      fontSize: 18,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      mt: 0.5,
                    }}
                  >
                    {title}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: theme.palette.secondary.main,
                    fontSize: 14,
                    mb: 1,
                    ml: 1.2,
                    textAlign: "left",
                  }}
                >
                  {description}
                </Typography>
              </Box>
            }
          >
            <Box
              component="button"
              type="button"
              aria-label={`Open info about ${title}`}
              aria-describedby={
                isDesktop ? undefined : `sr-description-${name}`
              }
              sx={{
                background: "none",
                border: "none",
                borderRadius: "100px",
                padding: "6px",
                marginLeft: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ":focus": {
                  outline: `1px dashed ${theme.palette.tertiary.main}`,
                  outlineOffset: -3,
                },
                ":hover": {
                  outline: `1px dashed ${theme.palette.tertiary.main}`,
                  outlineOffset: -3,
                },
              }}
            >
              <InformationIcon
                sx={{ fontSize: "1.6rem", color: theme.palette.tertiary.main }}
              />
            </Box>
          </Tooltip>
        ) : (
          <>
            <Box
              component="button"
              type="button"
              aria-label={`Open info popover about ${title}`}
              aria-expanded={isPopoverOpen}
              aria-describedby={
                isPopoverOpen ? `sr-description-${name}` : undefined
              }
              onClick={handlePopoverOpen}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handlePopoverOpen(e);
                }
              }}
              sx={{
                background: "none",
                border: "none",
                borderRadius: "100px",
                padding: "6px",
                marginLeft: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ":focus": {
                  outline: `1px dashed ${theme.palette.tertiary.main}`,
                  outlineOffset: -3,
                },
              }}
            >
              <InformationIcon
                sx={{ fontSize: "1.6rem", color: theme.palette.tertiary.main }}
              />
            </Box>
            <Box
              id={isPopoverOpen ? undefined : `sr-description-${name}`}
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "1px",
                height: "1px",
                margin: "-1px",
                padding: 0,
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                border: 0,
              }}
            >
              {description}
            </Box>
          </>
        )}
      </>
    );

    const sharedSx = {
      my: 1,
      "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
          borderColor: error
            ? theme.palette.darkRed
            : theme.palette.primary.main,
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: 16,
        color:
          error && value
            ? theme.palette.darkRed
            : error
              ? theme.palette.neutralGray
              : value
                ? theme.palette.primary.main
                : theme.palette.neutralGray,
        "&.Mui-focused": {
          color: error ? theme.palette.darkRed : theme.palette.primary.main,
        },
      },
    };

    return (
      <>
        {maskFormat ? (
          <PatternFormat
            ref={ref}
            value={value}
            onValueChange={(values) => onChange(values.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={handleFieldBlur}
            customInput={StyledTextField}
            format={maskFormat}
            fullWidth
            label={label}
            name={name}
            autoComplete={autoComplete}
            error={Boolean(error)}
            InputProps={{
              endAdornment: InfoButton,
              inputProps,
              notched: shouldShrinkLabel,
              sx: {
                color: error ? theme.palette.secondary.main : "inherit",
                fontSize: 16,
              },
            }}
            sx={sharedSx}
            InputLabelProps={{
              shrink: shouldShrinkLabel,
              sx: { fontSize: 16 },
            }}
          />
        ) : (
          <StyledTextField
            fullWidth
            label={label}
            name={name}
            autoComplete={autoComplete}
            inputRef={ref}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleFieldBlur}
            error={Boolean(error)}
            InputProps={{
              endAdornment: InfoButton,
              inputProps,
              notched: shouldShrinkLabel,
              sx: {
                color: error ? theme.palette.secondary.main : "inherit",
                fontSize: 16,
              },
            }}
            sx={sharedSx}
            InputLabelProps={{
              shrink: shouldShrinkLabel,
              sx: { fontSize: 16 },
            }}
          />
        )}

        <Popover
          open={!isDesktop && isPopoverOpen}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                p: 2,
                maxWidth: "min(300px, calc(100vw - 50px))",
                backgroundColor: theme.palette.lightPurple,
                boxShadow: theme.shadows[4],
                borderTop: `1px solid ${theme.palette.tertiary.main}`,
                borderBottom: `1px solid ${theme.palette.tertiary.main}`,
              },
            },
          }}
        >
          <Box role="tooltip" id={`sr-description-${name}`}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <InformationIcon
                  sx={{
                    fontSize: 24,
                    color: theme.palette.tertiary.main,
                    mr: 1,
                    mt: "7px",
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    color: theme.palette.tertiary.main,
                    fontSize: 18,
                    fontWeight: 700,
                    lineHeight: 1.3,
                    mt: 0.5,
                  }}
                >
                  {title}
                </Typography>
              </Box>

              <IconButton
                type="button"
                aria-label="Close"
                onClick={handlePopoverClose}
                disableRipple
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  ml: 2,
                  color: "common.black",
                  "&:focus": {
                    outline: `1px dashed ${theme.palette.tertiary.main}`,
                    outlineOffset: "-2px",
                  },
                }}
              >
                <CloseIcon
                  aria-hidden="true"
                  fill="inherit"
                  sx={{
                    fontSize: "20px",
                  }}
                />
              </IconButton>
            </Box>

            <Typography
              sx={{
                color: theme.palette.secondary.main,
                fontSize: 14,
                mb: 1,
                ml: 1.2,
                textAlign: "left",
              }}
            >
              {description}
            </Typography>
          </Box>
        </Popover>
      </>
    );
  },
);

SurveyTextField.displayName = "SurveyTextField";

SurveyTextField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
  description: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  inputProps: PropTypes.object,
  maskFormat: PropTypes.string,
};

export default SurveyTextField;
